import React, { useState } from "react";
import { Box, Typography, Chip, Collapse, IconButton } from "@mui/material";
import { useDraggable } from "@dnd-kit/core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { scalarFields, collectionFields, recordListField, sourceLabel } from "../fieldCatalog";

function FieldChip({ field, dndId, color = "info", collectionPath, collLabel }) {
  // Child chips carry their parent collection so a drop knows the column's level.
  const dragField = collectionPath ? { ...field, _collectionPath: collectionPath, _collLabel: collLabel } : field;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndId,
    data: { kind: "FIELD", field: dragField },
  });
  return (
    <Chip
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      size="small"
      color={color}
      variant="outlined"
      label={field.label}
      title={field.path}
      sx={{
        cursor: "grab",
        fontWeight: 600,
        opacity: isDragging ? 0.4 : 1,
        "&:hover": { boxShadow: 1 },
      }}
    />
  );
}

export default function FieldTreePanel({ source }) {
  const scalars = scalarFields(source);
  const colls = collectionFields(source);
  const [open, setOpen] = useState({});
  const isOpen = (path) => (open[path] === undefined ? true : open[path]);

  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        Data fields · drag to map
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <FieldChip field={recordListField(source)} dndId="field-recordlist" color="warning" />
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
          Drop on a Data table → one row per {sourceLabel(source)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {scalars.map((f, i) => (
          <FieldChip key={f.path} field={f} dndId={`field-scalar-${i}-${f.path}`} />
        ))}
      </Box>

      {colls.map((c, ci) => (
        <Box
          key={c.path}
          sx={{ mt: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 0.5,
              py: 0.5,
              bgcolor: "action.hover",
            }}
          >
            <IconButton size="small" onClick={() => setOpen((o) => ({ ...o, [c.path]: !isOpen(c.path) }))}>
              {isOpen(c.path) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
            <FieldChip field={c} dndId={`field-coll-${ci}-${c.path}`} color="primary" />
          </Box>
          <Collapse in={isOpen(c.path)} timeout="auto" unmountOnExit>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, p: 1 }}>
              {(c.children || []).map((ch, i) => (
                <FieldChip
                  key={ch.path}
                  field={ch}
                  dndId={`field-${c.path}-${i}-${ch.path}`}
                  collectionPath={c.path}
                  collLabel={c.label}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      ))}
    </Box>
  );
}
